import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiZap, FiRefreshCw, FiPlus, FiTrash2, FiEdit2, FiX, FiCheck,
  FiActivity, FiCode, FiAlertCircle, FiCheckCircle, FiClock,
  FiToggleLeft, FiToggleRight, FiDownload, FiPlay, FiAlertTriangle,
} from 'react-icons/fi';
import {
  getAdminLanes, createAdminLane, updateAdminLane, deleteAdminLane,
  getAdminLaneManifest, refreshAdminLaneHealth, refreshAllLanesHealth,
  getAdminOrganizations, streamActivateLane, backfillEnterpriseLanes,
} from '../../api/admin';
import type { TenantExecutionLane, LaneCreatePayload, LaneProvisionEvent } from '../../api/admin';

// ── Helpers ─────────────────────────────────────────────────────────────────

function ProvisionBadge({ status }: { status: string }) {
  switch (status) {
    case 'ready':
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700 border border-green-200 flex items-center gap-1">
          <FiCheckCircle size={10} /> Prête
        </span>
      );
    case 'provisioning':
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
          <FiRefreshCw size={10} className="animate-spin" /> En cours
        </span>
      );
    case 'error':
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700 border border-red-200 flex items-center gap-1">
          <FiAlertCircle size={10} /> Erreur
        </span>
      );
    default:
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500 border border-gray-200 flex items-center gap-1">
          <FiClock size={10} /> Non provisionné
        </span>
      );
  }
}

function SlotsBar({ active, max, online }: { active: number; max: number; online: number }) {
  const effective = Math.min(max, online);
  const pct = effective > 0 ? Math.min(100, (active / effective) * 100) : 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 60 ? 'bg-orange-400' : 'bg-green-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 whitespace-nowrap">{active}/{effective}</span>
    </div>
  );
}

// ── Step icons & couleurs ────────────────────────────────────────────────────

const STEP_LABELS: Record<string, string> = {
  init:         'Initialisation',
  ado_pool:     '1. Création pool ADO',
  db_status:    '2. Lane DB (status=provisioning)',
  k8s_manifest: '3. Génération manifest K8s',
  k8s_apply:    '4. Déploiement agents K8s',
  agents_poll:  '5. Attente agents online',
  done:         '6. Validation finale',
  error:        'Erreur',
};

function StepRow({ event }: { event: LaneProvisionEvent }) {
  const icon =
    event.status === 'running'  ? <FiRefreshCw size={13} className="animate-spin text-blue-500" /> :
    event.status === 'success'  ? <FiCheckCircle size={13} className="text-green-600" /> :
    event.status === 'warning'  ? <FiAlertTriangle size={13} className="text-orange-500" /> :
                                  <FiAlertCircle size={13} className="text-red-600" />;

  const bg =
    event.status === 'running'  ? 'bg-blue-50  border-blue-100' :
    event.status === 'success'  ? 'bg-green-50 border-green-100' :
    event.status === 'warning'  ? 'bg-orange-50 border-orange-100' :
                                  'bg-red-50   border-red-100';

  return (
    <div className={`flex items-start gap-2.5 p-2.5 rounded-lg border text-sm ${bg}`}>
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-gray-800 text-xs">
          {STEP_LABELS[event.step] ?? event.step}
        </div>
        <div className="text-gray-600 text-xs mt-0.5 break-words">{event.message}</div>
      </div>
    </div>
  );
}

// ── Modal provisioning SSE ────────────────────────────────────────────────────

function ActivateModal({ lane, onClose, onDone }: {
  lane:    TenantExecutionLane;
  onClose: () => void;
  onDone:  () => void;
}) {
  const [events, setEvents]   = useState<LaneProvisionEvent[]>([]);
  const [done,   setDone]     = useState(false);
  const [started, setStarted] = useState(false);
  const cleanupRef            = useRef<(() => void) | null>(null);
  const bottomRef             = useRef<HTMLDivElement | null>(null);

  const start = () => {
    setStarted(true);
    setEvents([]);
    const cleanup = streamActivateLane(lane.id, (ev) => {
      setEvents(prev => [...prev, ev]);
      if (ev.step === 'done' || ev.step === 'error') {
        setDone(true);
        onDone();
      }
    });
    cleanupRef.current = cleanup;
  };

  useEffect(() => () => { cleanupRef.current?.(); }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const lastEvent = events[events.length - 1];
  const isDone    = done;
  const isError   = lastEvent?.step === 'error';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FiZap className="text-purple-600" size={16} /> Activer Enterprise Lane
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{lane.label}</p>
          </div>
          {isDone && (
            <button onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition">
              <FiX size={16} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5 space-y-2">
          {!started ? (
            <div className="text-center py-8">
              <FiZap size={36} className="text-purple-400 mx-auto mb-3" />
              <p className="text-gray-700 font-medium mb-1">Prêt à provisionner la lane</p>
              <p className="text-sm text-gray-500 mb-4">
                Cela va créer le pool ADO, générer le manifest K8s et attendre les agents.<br />
                La lane sera active automatiquement dès que les agents sont en ligne.
              </p>
              <div className="bg-gray-50 rounded-lg p-3 text-left text-xs text-gray-600 space-y-1 mb-5">
                <div>🔹 Org ADO : <span className="font-mono">{lane.ado_organization}</span></div>
                <div>🔹 Pool    : <span className="font-mono">{lane.ado_pool_name}</span></div>
                <div>🔹 Max concurrence : <strong>{lane.max_concurrency}</strong></div>
                <div>🔹 Agents min requis : <strong>{lane.min_agents_required}</strong></div>
              </div>
              <button onClick={start}
                className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition mx-auto font-medium">
                <FiPlay size={14} /> Démarrer le provisioning
              </button>
            </div>
          ) : (
            <>
              {events.map((ev, i) => (
                <StepRow key={i} event={ev} />
              ))}
              {!isDone && (
                <div className="flex items-center gap-2 text-xs text-gray-400 py-1 px-2">
                  <FiRefreshCw size={11} className="animate-spin" />
                  En cours… (la lane se mettra en route automatiquement dès que les agents sont prêts)
                </div>
              )}
              <div ref={bottomRef} />
            </>
          )}
        </div>

        {/* Footer */}
        {isDone && (
          <div className={`px-6 py-4 border-t ${isError ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'} rounded-b-2xl`}>
            <div className="flex items-center justify-between">
              <span className={`text-sm font-medium ${isError ? 'text-red-700' : 'text-green-700'} flex items-center gap-2`}>
                {isError
                  ? <><FiAlertCircle size={14} /> Provisioning échoué — voir les détails ci-dessus</>
                  : <><FiCheckCircle size={14} /> Provisioning terminé</>
                }
              </span>
              <button onClick={onClose}
                className="px-4 py-1.5 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition">
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modal Manifest ────────────────────────────────────────────────────────────

function ManifestModal({ laneId, onClose }: { laneId: string; onClose: () => void }) {
  const [loading,   setLoading]   = useState(true);
  const [manifest,  setManifest]  = useState('');
  const [namespace, setNamespace] = useState('');
  const [error,     setError]     = useState<string | null>(null);

  const load = async (regen = false) => {
    setLoading(true); setError(null);
    try {
      const r = await getAdminLaneManifest(laneId, regen);
      setManifest(r.manifest);
      setNamespace(r.namespace);
    } catch {
      setError('Impossible de charger le manifest');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [laneId]);

  const download = () => {
    const blob = new Blob([manifest], { type: 'text/yaml' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${namespace || 'lane'}-k8s.yaml`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-900">Manifest Kubernetes</h3>
            {namespace && <p className="text-xs text-gray-500 mt-0.5">Namespace : {namespace}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(true)} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50">
              <FiRefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Regénérer
            </button>
            <button onClick={download} disabled={!manifest}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50">
              <FiDownload size={13} /> Télécharger
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition">
              <FiX size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-gray-400">
              <FiRefreshCw className="animate-spin mr-2" /> Chargement…
            </div>
          ) : error ? (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          ) : (
            <pre className="text-xs font-mono bg-gray-950 text-green-300 p-4 rounded-xl overflow-x-auto whitespace-pre">
              {manifest}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Formulaire création / édition ─────────────────────────────────────────────

interface LaneFormProps {
  orgs:     { id: string; name: string; slug: string }[];
  initial?: Partial<LaneCreatePayload & { id: string; is_active: boolean }>;
  onSave:   (data: LaneCreatePayload) => Promise<void>;
  onCancel: () => void;
  saving:   boolean;
}

function LaneForm({ orgs, initial, onSave, onCancel, saving }: LaneFormProps) {
  const [form, setForm] = useState<LaneCreatePayload>({
    organization_id:      initial?.organization_id     ?? '',
    label:                initial?.label               ?? '',
    ado_organization:     initial?.ado_organization    ?? 'GenIaC-Enterprise',
    ado_pool_name:        initial?.ado_pool_name        ?? '',
    ado_project:          initial?.ado_project          ?? 'GenIaC',
    max_concurrency:      initial?.max_concurrency      ?? 2,   // default standard Enterprise
    min_agents_required:  initial?.min_agents_required  ?? 1,  // 1 agent suffit pour démarrer
    k8s_namespace:        initial?.k8s_namespace        ?? '',
    notes:                initial?.notes                ?? '',
  });

  const set = (k: keyof LaneCreatePayload, v: unknown) =>
    setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Organisation *</label>
          <select value={form.organization_id} onChange={e => set('organization_id', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400">
            <option value="">Sélectionner…</option>
            {orgs.map(o => (
              <option key={o.id} value={o.id}>{o.name} ({o.slug})</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Label *</label>
          <input value={form.label} onChange={e => set('label', e.target.value)}
            placeholder="ex: Acme Corp — Lane Principale"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Organisation ADO *</label>
          <input value={form.ado_organization} onChange={e => set('ado_organization', e.target.value)}
            placeholder="GenIaC-Enterprise"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Nom du Pool ADO *</label>
          <input value={form.ado_pool_name} onChange={e => set('ado_pool_name', e.target.value)}
            placeholder="acmecorp-agents"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Projet ADO</label>
          <input value={form.ado_project} onChange={e => set('ado_project', e.target.value)}
            placeholder="GenIaC"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Namespace K8s</label>
          <input value={form.k8s_namespace ?? ''} onChange={e => set('k8s_namespace', e.target.value)}
            placeholder="auto-généré si vide"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Concurrence max</label>
          <input type="number" min={1} max={50} value={form.max_concurrency}
            onChange={e => set('max_concurrency', parseInt(e.target.value) || 2)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Agents min requis</label>
          <input type="number" min={1} max={50} value={form.min_agents_required}
            onChange={e => set('min_agents_required', parseInt(e.target.value) || 1)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
          <textarea value={form.notes ?? ''} onChange={e => set('notes', e.target.value)}
            rows={2} placeholder="Notes internes…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">
          Annuler
        </button>
        <button onClick={() => onSave(form)}
          disabled={saving || !form.organization_id || !form.label || !form.ado_pool_name}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50">
          {saving ? <FiRefreshCw size={13} className="animate-spin" /> : <FiCheck size={13} />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

const AdminEnterpriseLanes: React.FC = () => {
  const [lanes,    setLanes]    = useState<TenantExecutionLane[]>([]);
  const [orgs,     setOrgs]     = useState<{ id: string; name: string; slug: string }[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [editing,  setEditing]  = useState<string | null>(null);
  const [manifest, setManifest] = useState<string | null>(null);
  const [activating,  setActivating]  = useState<TenantExecutionLane | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState<string | null>(null);
  const [busyIds,     setBusyIds]     = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [lanesData, orgsData] = await Promise.all([
        getAdminLanes(),
        getAdminOrganizations({ limit: 200, plan: 'entreprise' }),
      ]);
      const enterpriseOrgIds = new Set((orgsData.organizations || []).map((o: any) => o.id as string));
      setLanes(lanesData.filter((l: TenantExecutionLane) => enterpriseOrgIds.has(l.organization_id)));
      setOrgs((orgsData.organizations || []).map((o: any) => ({ id: o.id, name: o.name, slug: o.slug })));
    } catch {
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setBusy   = (id: string) => setBusyIds(s => new Set(s).add(id));
  const clearBusy = (id: string) => setBusyIds(s => { const n = new Set(s); n.delete(id); return n; });

  const handleCreate = async (data: LaneCreatePayload) => {
    setSaving(true);
    try {
      await createAdminLane(data);
      setCreating(false);
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: string, data: LaneCreatePayload) => {
    setSaving(true);
    try {
      await updateAdminLane(id, data);
      setEditing(null);
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (lane: TenantExecutionLane) => {
    setBusy(lane.id);
    try {
      await updateAdminLane(lane.id, { is_active: !lane.is_active });
      await load();
    } finally {
      clearBusy(lane.id);
    }
  };

  const handleDelete = async (lane: TenantExecutionLane) => {
    if (!window.confirm(`Supprimer la lane "${lane.label}" ? Cette action est irréversible.`)) return;
    setBusy(lane.id);
    try {
      await deleteAdminLane(lane.id);
      await load();
    } finally {
      clearBusy(lane.id);
    }
  };

  const handleRefreshHealth = async (lane: TenantExecutionLane) => {
    setBusy(lane.id);
    try {
      await refreshAdminLaneHealth(lane.id);
      await load();
    } finally {
      clearBusy(lane.id);
    }
  };

  const handleRefreshAll = async () => {
    setLoading(true);
    try {
      await refreshAllLanesHealth();
      await load();
    } finally {
      setLoading(false);
    }
  };

  const handleBackfill = async () => {
    setBackfilling(true); setBackfillMsg(null);
    try {
      const r = await backfillEnterpriseLanes();
      if (r.total === 0) {
        setBackfillMsg('Toutes les orgs Enterprise ont déjà une lane active.');
      } else {
        const ok  = r.results.filter(x => x.status !== 'error').length;
        const err = r.results.filter(x => x.status === 'error').length;
        setBackfillMsg(`${ok} lane(s) créée(s) et provisionnement démarré${err ? ` — ${err} erreur(s)` : ''}.`);
        await load();
      }
    } catch {
      setBackfillMsg('Erreur lors du backfill.');
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiZap className="text-purple-600" /> Enterprise Execution Lanes
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Lanes dédiées par tenant — pool ADO isolé + agents K8s dédiés
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleBackfill} disabled={backfilling || loading}
            title="Crée automatiquement les lanes pour les orgs Enterprise qui n'en ont pas encore"
            className="flex items-center gap-2 px-3 py-1.5 border border-purple-300 rounded-lg text-sm text-purple-700 hover:bg-purple-50 transition disabled:opacity-50">
            <FiZap size={14} className={backfilling ? 'animate-pulse' : ''} />
            {backfilling ? 'Synchronisation…' : 'Sync clients existants'}
          </button>
          <button onClick={handleRefreshAll} disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition disabled:opacity-50">
            <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh all
          </button>
          <button onClick={() => setCreating(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition">
            <FiPlus size={14} /> Nouvelle lane
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <FiAlertCircle size={14} /> {error}
        </div>
      )}

      {backfillMsg && (
        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-purple-800 text-sm flex items-center justify-between">
          <span className="flex items-center gap-2"><FiCheckCircle size={14} /> {backfillMsg}</span>
          <button onClick={() => setBackfillMsg(null)} className="text-purple-400 hover:text-purple-700">
            <FiX size={14} />
          </button>
        </div>
      )}

      {/* ── Formulaire création ── */}
      {creating && (
        <div className="bg-white rounded-xl border border-purple-200 p-5 mb-4 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiPlus size={15} className="text-purple-600" /> Nouvelle lane Enterprise
          </h3>
          <LaneForm
            orgs={orgs}
            onSave={handleCreate}
            onCancel={() => setCreating(false)}
            saving={saving}
          />
        </div>
      )}

      {/* ── Liste lanes ── */}
      {loading && !lanes.length ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
          <FiRefreshCw className="animate-spin inline mr-2" size={16} /> Chargement…
        </div>
      ) : lanes.length === 0 && !creating ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FiZap size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Aucune lane Enterprise configurée</p>
          <button onClick={() => setCreating(true)} className="mt-3 text-sm text-purple-600 hover:underline">
            Créer la première lane
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {lanes.map(lane => (
            <div key={lane.id}
              className={`bg-white rounded-xl border ${lane.is_active ? 'border-gray-200' : 'border-gray-100 opacity-70'} shadow-sm overflow-hidden`}>

              {editing === lane.id ? (
                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FiEdit2 size={14} className="text-purple-600" /> Modifier la lane
                  </h3>
                  <LaneForm
                    orgs={orgs}
                    initial={{ ...lane, k8s_namespace: lane.k8s_namespace ?? undefined, notes: lane.notes ?? undefined }}
                    onSave={(data) => handleUpdate(lane.id, data)}
                    onCancel={() => setEditing(null)}
                    saving={saving}
                  />
                </div>
              ) : (
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Info principale */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-gray-900 truncate">{lane.label}</span>
                        <ProvisionBadge status={(lane as any).provision_status ?? 'pending'} />
                        {!lane.is_active && (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full">Désactivé</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                        <span className="font-mono">{lane.ado_organization} / {lane.ado_pool_name}</span>
                        {lane.ado_pool_id && <span className="text-gray-400">pool #{lane.ado_pool_id}</span>}
                        <span>·</span>
                        <span>{lane.organization_name}</span>
                        {lane.k8s_namespace && (
                          <span className="font-mono text-blue-600">{lane.k8s_namespace}</span>
                        )}
                      </div>
                    </div>

                    {/* Métriques */}
                    <div className="flex items-center gap-6 text-center shrink-0">
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Agents online</div>
                        <span className={`text-sm font-bold ${lane.agents_online >= lane.min_agents_required ? 'text-green-600' : 'text-red-500'}`}>
                          {lane.agents_online}
                          <span className="text-xs font-normal text-gray-400">/{lane.min_agents_required} min</span>
                        </span>
                      </div>
                      <div className="w-28">
                        <div className="text-xs text-gray-400 mb-1">Slots ({lane.max_concurrency})</div>
                        <SlotsBar
                          active={Number((lane as any).current_jobs ?? lane.active_jobs)}
                          max={lane.max_concurrency}
                          online={lane.agents_online}
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      {/* Activer Enterprise (provisioning complet) */}
                      <button
                        onClick={() => setActivating(lane)}
                        disabled={busyIds.has(lane.id)}
                        title="Activer Enterprise (provisioning complet)"
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg transition font-medium
                          ${(lane as any).provision_status === 'ready'
                            ? 'text-green-700 bg-green-50 hover:bg-green-100 border border-green-200'
                            : 'text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200'
                          }`}
                      >
                        <FiPlay size={12} />
                        {(lane as any).provision_status === 'ready' ? 'Re-provisionner' : 'Activer'}
                      </button>

                      {/* Refresh health */}
                      <button onClick={() => handleRefreshHealth(lane)} disabled={busyIds.has(lane.id)}
                        title="Rafraîchir agents online"
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-40">
                        <FiActivity size={14} className={busyIds.has(lane.id) ? 'animate-pulse' : ''} />
                      </button>

                      {/* Manifest K8s */}
                      <button onClick={() => setManifest(lane.id)}
                        title="Manifest Kubernetes"
                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition">
                        <FiCode size={14} />
                      </button>

                      {/* Toggle actif */}
                      <button onClick={() => handleToggleActive(lane)} disabled={busyIds.has(lane.id)}
                        title={lane.is_active ? 'Désactiver' : 'Activer'}
                        className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition disabled:opacity-40">
                        {lane.is_active
                          ? <FiToggleRight size={16} className="text-green-500" />
                          : <FiToggleLeft  size={16} />
                        }
                      </button>

                      {/* Éditer */}
                      <button onClick={() => setEditing(lane.id)}
                        title="Modifier"
                        className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition">
                        <FiEdit2 size={14} />
                      </button>

                      {/* Supprimer */}
                      <button onClick={() => handleDelete(lane)} disabled={busyIds.has(lane.id)}
                        title="Supprimer"
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-40">
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Erreur de provisioning */}
                  {(lane as any).provision_status === 'error' && (lane as any).provision_error && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700 flex items-start gap-1.5">
                      <FiAlertCircle size={12} className="mt-0.5 shrink-0" />
                      {(lane as any).provision_error}
                    </div>
                  )}

                  {lane.notes && (
                    <p className="mt-2 text-xs text-gray-400 italic border-t border-gray-50 pt-2">{lane.notes}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Info box ── */}
      {lanes.length > 0 && (
        <div className="mt-6 bg-purple-50 border border-purple-200 rounded-xl p-4 text-sm text-purple-800">
          <strong>Flux de provisioning :</strong>{' '}
          Cliquez sur <strong>Activer</strong> pour déclencher automatiquement les 6 étapes :
          pool ADO → manifest K8s → déploiement agents → poll online → <code className="text-xs bg-purple-100 px-1 rounded">status=ready</code>.
          <br />
          <strong>effectiveLimit = min(max_concurrency, agents_online)</strong> — le dispatcher ne dispatche que si des agents sont disponibles.
        </div>
      )}

      {/* ── Modals ── */}
      {manifest && (
        <ManifestModal laneId={manifest} onClose={() => setManifest(null)} />
      )}

      {activating && (
        <ActivateModal
          lane={activating}
          onClose={() => { setActivating(null); }}
          onDone={() => load()}
        />
      )}
    </div>
  );
};

export default AdminEnterpriseLanes;
